import { useCallback, useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { userRoles } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash, ArrowCounterClockwise, MagnifyingGlass, FileText } from "@phosphor-icons/react";
import { NewDocumentDialog, NewVersionUpload, VersionList, SharePanel, fmtDate } from "@/components/dms/DmsParts";

function DocumentDetail({ docId, onClose, onChanged, isAdmin }) {
  const [doc, setDoc] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await client.get(`/dms/documents/${docId}`); setDoc(data); }
    catch (e) { toast.error(apiError(e)); onClose(); }
  }, [docId, onClose]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged(); };
  const softDelete = async () => {
    if (!window.confirm("Mark this document as deleted? It will be hidden from all users.")) return;
    try { await client.delete(`/dms/documents/${docId}`); toast.success("Document deleted"); onChanged(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dms-detail-dialog">
        {!doc ? <div className="py-10 text-center text-slate-500">Loading…</div> : (
          <div className="grid gap-5">
            <DialogHeader>
              <DialogTitle data-testid="dms-detail-title">{doc.title}</DialogTitle>
              {doc.description && <p className="text-sm text-slate-600 whitespace-pre-wrap">{doc.description}</p>}
              <p className="text-xs text-slate-500">Owner: {doc.owner_name} · Created {fmtDate(doc.created_at)} · Latest v{doc.latest_version}</p>
            </DialogHeader>
            {!doc.is_deleted && (
              <div><div className="label-cap mb-1.5">Upload new version</div>
                <NewVersionUpload docId={doc.id} nextVersion={doc.latest_version + 1} onUploaded={refresh} /></div>
            )}
            <div><div className="label-cap mb-1.5">Version history</div><VersionList doc={doc} /></div>
            {doc.can_share && !doc.is_deleted && (
              <div><div className="label-cap mb-1.5">Share with (view, download & upload new versions)</div>
                <SharePanel doc={doc} onSaved={refresh} /></div>
            )}
            {isAdmin && !doc.is_deleted && (
              <Button data-testid="dms-delete-btn" variant="outline" onClick={softDelete} className="rounded-sm text-rose-600 border-rose-200 hover:bg-rose-50 justify-self-start">
                <Trash size={16} className="mr-1" /> Delete document
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocTable({ rows, onOpen, deleted, onRestore }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
          <th className="px-4 py-3">Title</th><th className="px-4 py-3">Latest File</th><th className="px-4 py-3">Version</th>
          <th className="px-4 py-3">Owner</th><th className="px-4 py-3">{deleted ? "Deleted" : "Updated"}</th>
          {deleted && <th className="px-4 py-3 w-28">Action</th>}
        </tr></thead>
        <tbody data-testid={deleted ? "dms-deleted-tbody" : "dms-tbody"}>
          {rows.map((d) => (
            <tr key={d.id} data-testid={`dms-row-${d.id}`} onClick={() => onOpen(d.id)} className="border-b border-slate-100 hover:bg-orange-50/50 cursor-pointer">
              <td className="px-4 py-2"><div className="font-semibold flex items-center gap-2"><FileText size={16} className="text-orange-600 shrink-0" />{d.title}</div>
                {d.description && <div className="text-xs text-slate-500 truncate max-w-xs">{d.description}</div>}</td>
              <td className="px-4 py-2 text-slate-600 truncate max-w-[200px]">{d.latest_filename}</td>
              <td className="px-4 py-2"><span className="px-2 py-0.5 text-xs font-bold rounded-sm bg-emerald-100 text-emerald-800">v{d.latest_version}</span></td>
              <td className="px-4 py-2">{d.owner_name}{d.shared_with.length > 0 && <span className="ml-1 text-xs text-slate-400">+{d.shared_with.length} shared</span>}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{deleted ? `${fmtDate(d.deleted_at)} by ${d.deleted_by_name}` : fmtDate(d.updated_at)}</td>
              {deleted && <td className="px-4 py-2">
                <Button size="sm" data-testid={`dms-restore-${d.id}`} onClick={(e) => { e.stopPropagation(); onRestore(d.id); }} className="rounded-sm bg-emerald-600 hover:bg-emerald-700">
                  <ArrowCounterClockwise size={14} className="mr-1" /> Restore</Button></td>}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No documents</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [tab, setTab] = useState("active");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = tab === "deleted" ? await client.get("/dms/deleted") : await client.get("/dms/documents", { params: { q } });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [tab, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const closeDetail = useCallback(() => setDetailId(null), []);

  const restore = async (id) => {
    try { await client.post(`/dms/documents/${id}/restore`); toast.success("Document restored"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Documents" subtitle="Versioned file library"
        actions={<Button data-testid="dms-new-btn" onClick={() => setOpenNew(true)} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} weight="bold" className="mr-1" /> New Document</Button>} />
      <Section className="grid gap-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          {isAdmin && (
            <div className="flex gap-1" data-testid="dms-tabs">
              {[["active", "Documents"], ["deleted", "Deleted"]].map(([k, l]) => (
                <button key={k} data-testid={`dms-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
          )}
          {tab === "active" && (
            <div className="relative sm:max-w-sm w-full">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="dms-search-input" placeholder="Search title, description, file…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
            </div>
          )}
        </div>
        <DocTable rows={rows} onOpen={setDetailId} deleted={tab === "deleted"} onRestore={restore} />
      </Section>
      <NewDocumentDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />
      {detailId && <DocumentDetail docId={detailId} onClose={closeDetail} onChanged={load} isAdmin={isAdmin} />}
    </div>
  );
}
